import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ItemsTable from './components/ItemsTable';
import ItemFilters from './components/ItemFilters';
import InventoryAlerts from './components/InventoryAlerts';
import ItemFormModal from './components/ItemFormModal';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import { useAuth } from '../../hooks/useAuth';
import { itemsManagementService } from '../../services/itemsManagementService';

const ItemsManagement = () => {
  const { user, userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    stockStatus: 'all',
    status: 'all',
    minPrice: '',
    maxPrice: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Archive confirmation modal (existing)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Delete confirmation modal (new)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [bulkDeleteItems, setBulkDeleteItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [isExporting, setIsExporting] = useState(false);

  // Analytics data - load separately for better performance
  const [analytics, setAnalytics] = useState({
    totalItems: 0,
    totalValue: 0,
    monthlyGrowth: 0,
    currentMonthItems: 0,
    reorderSuggestions: []
  });

  // FIXED: Ensure categories useState is properly declared with both getter and setter
  const [categories, setCategories] = useState([
    "Electronics",
    "Furniture", 
    "Office Supplies",
    "Services",
    "Software",
    "Hardware"
  ]);

  const [lowStockItems, setLowStockItems] = useState([]);

  // CRITICAL FIX: Correct organization ID access pattern
  const organizationId = userProfile?.organization_id || user?.user_metadata?.organizationId;

  // Add debug logging to help troubleshoot
  useEffect(() => {
    console.debug('Items Management Debug:', {
      hasUser: !!user,
      hasUserProfile: !!userProfile,
      organizationId,
      userProfileStructure: userProfile ? Object.keys(userProfile) : null,
      authInitialized: userProfile !== null
    });
  }, [user, userProfile, organizationId]);

  // CRITICAL FIX: Better loading state handling
  const isReady = user && userProfile && organizationId && !userProfile?.isFallback;
  
  // Move getStockStatus function before processedItems useMemo to fix initialization error
  const getStockStatus = (stock, threshold) => {
    if (stock === 0) return 'out-of-stock';
    if (stock <= threshold) return 'low-stock';
    return 'in-stock';
  };
  
  // Optimized data loading with separate functions
  const loadItemsData = useCallback(async (useCurrentFilters = false) => {
    if (!organizationId) {
      console.warn('No organization ID available for loading items');
      setError('No organization context available. Please check your account setup.');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(!initialDataLoaded); // Only show loading for initial load
      setError(null);

      const filtersToUse = useCurrentFilters ? filters : {};
      let result = await itemsManagementService?.getItems(organizationId, filtersToUse);
      
      if (!result) {
        throw new Error('Service returned null result');
      }

      const { data, error: fetchError } = result;
      
      if (fetchError) {
        setError('Failed to load items: ' + (fetchError?.message || 'Unknown error'));
        console.error('Error loading items:', fetchError);
        return;
      }

      setItems(data || []);
      if (!initialDataLoaded) {
        setInitialDataLoaded(true);
      }
      
    } catch (err) {
      const errorMessage = err?.message || 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error in loadItemsData:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters, initialDataLoaded]);

  const loadAnalytics = useCallback(async () => {
    if (!organizationId) return;
    
    try {
      const analyticsData = await itemsManagementService?.getInventoryAnalytics(organizationId);
      setAnalytics(analyticsData || {
        totalItems: 0,
        totalValue: 0,
        monthlyGrowth: 0,
        currentMonthItems: 0,
        reorderSuggestions: []
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  }, [organizationId]);

  // FIXED: Corrected loadCategories function with proper dependency array
 const loadCategories = useCallback(async () => {
  if (!organizationId) return;

  try {
    const categoriesData = await itemsManagementService?.getCategories(organizationId);

    setCategories(prev => {
      // Normalize: support string or object ({name}/{label})
      const fromApi = Array.isArray(categoriesData)
        ? categoriesData?.map(c =>
              typeof c === "string" ? c : (c?.name || c?.label || "")
            )?.filter(Boolean)
        : [];

      // Merge with your static defaults, de-dup, sort A→Z
      const merged = Array.from(new Set([
        ...prev,
        ...fromApi,
      ]));

      return merged?.sort((a, b) => a?.localeCompare(b));
    });
  } catch (err) {
    console.error("Error loading categories:", err);
    // Keep defaults on failure
  }
}, [organizationId]);


  const loadLowStockItems = useCallback(async () => {
    if (!organizationId) return;
    
    try {
      const lowStock = await itemsManagementService?.getLowStockItems(organizationId);
      setLowStockItems(lowStock || []);
    } catch (err) {
      console.error('Error loading low stock items:', err);
    }
  }, [organizationId]);

  // CRITICAL FIX: Only load data when we have valid organization context
  useEffect(() => {
    if (isReady) {
      console.debug('Loading items data for organization:', organizationId);
      // Load items first (most important), then load other data in parallel
      loadItemsData()?.then(() => {
        // Load secondary data in parallel
        Promise.all([
          loadAnalytics(),
          loadCategories(),
          loadLowStockItems()
        ])?.catch(err => {
          console.warn('Error loading secondary data:', err);
        });
      })?.catch(err => {
        console.error('Error loading primary data:', err);
      });
    } else {
      console.debug('Not ready to load data:', { isReady, user: !!user, userProfile: !!userProfile, organizationId });
    }
  }, [isReady, organizationId, loadItemsData, loadAnalytics, loadCategories, loadLowStockItems]);

  // Debounced real-time subscription
  useEffect(() => {
    if (!organizationId || !initialDataLoaded) return;

    const subscription = itemsManagementService?.subscribeToItems(
      organizationId,
      (payload) => {
        console.log('Real-time update:', payload);
        // Only refresh if it's a significant change
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'DELETE') {
          loadItemsData(true); // Use current filters
        }
      }
    );

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [organizationId, initialDataLoaded, loadItemsData]);

  // Client-side filtering and sorting for better performance
  const processedItems = useMemo(() => {
    let filtered = items?.filter(item => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        if (!item?.name?.toLowerCase()?.includes(searchTerm) && 
            !item?.sku?.toLowerCase()?.includes(searchTerm) &&
            !item?.description?.toLowerCase()?.includes(searchTerm)) {
          return false;
        }
      }

      // Category filter
      if (filters?.category !== 'all' && item?.category !== filters?.category) {
        return false;
      }

      // Stock status filter
      if (filters?.stockStatus !== 'all') {
        const stockStatus = getStockStatus(item?.stock, item?.lowStockThreshold);
        if (stockStatus !== filters?.stockStatus) {
          return false;
        }
      }

      // Status filter
      if (filters?.status !== 'all' && item?.status !== filters?.status) {
        return false;
      }

      // Price range filter
      if (filters?.minPrice && item?.price < parseFloat(filters?.minPrice)) {
        return false;
      }
      if (filters?.maxPrice && item?.price > parseFloat(filters?.maxPrice)) {
        return false;
      }

      return true;
    });

    // Sort items
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [items, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Optimized filter change handler - no server requests for client-side filtering
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // No need to reload data, filtering is done client-side now
  }, []);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      stockStatus: 'all',
      status: 'all',
      minPrice: '',
      maxPrice: ''
    });
  };

  // Show enhanced loading state with skeleton
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!userProfile || userProfile?.isFallback) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon name="AlertCircle" size={32} className="text-warning mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Profile Setup Required</h2>
          <p className="text-muted-foreground mb-4">
            {userProfile?.errorMessage || 'Unable to load your profile. This may be due to network issues or account setup.'}
          </p>
          <div className="space-y-2">
            <Button onClick={() => window.location?.reload()} variant="outline">
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon name="Building" size={32} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Organization Access</h2>
          <p className="text-muted-foreground mb-4">
            You don't have access to any organization or your organization membership is still being set up.
          </p>
          <div className="space-y-2">
            <Button onClick={() => window.location?.reload()} variant="outline">
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !initialDataLoaded) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="flex h-[calc(100vh-4rem)]">
            {/* Main Content Loading Skeleton */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto">
                <div className="p-6">
                  {/* Header Skeleton */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="h-8 bg-muted animate-pulse rounded-md w-64 mb-2"></div>
                      <div className="h-4 bg-muted animate-pulse rounded-md w-96"></div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-10 bg-muted animate-pulse rounded-md w-32"></div>
                      <div className="h-10 bg-muted animate-pulse rounded-md w-24"></div>
                      <div className="h-10 bg-muted animate-pulse rounded-md w-28"></div>
                    </div>
                  </div>
                  
                  {/* Filters Skeleton */}
                  <div className="bg-card border border-border rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="h-10 bg-muted animate-pulse rounded-md"></div>
                      <div className="h-10 bg-muted animate-pulse rounded-md"></div>
                      <div className="h-10 bg-muted animate-pulse rounded-md"></div>
                      <div className="h-10 bg-muted animate-pulse rounded-md"></div>
                    </div>
                  </div>

                  {/* Table Skeleton */}
                  <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="p-4">
                      <div className="space-y-4">
                        {[...Array(8)]?.map((_, i) => (
                          <div key={i} className="flex items-center space-x-4">
                            <div className="h-4 bg-muted animate-pulse rounded w-8"></div>
                            <div className="h-4 bg-muted animate-pulse rounded flex-1"></div>
                            <div className="h-4 bg-muted animate-pulse rounded w-24"></div>
                            <div className="h-4 bg-muted animate-pulse rounded w-20"></div>
                            <div className="h-4 bg-muted animate-pulse rounded w-16"></div>
                            <div className="h-4 bg-muted animate-pulse rounded w-12"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar Loading Skeleton */}
            <div className="w-80 border-l border-border bg-card overflow-y-auto">
              <div className="p-6">
                <div className="space-y-6">
                  <div className="h-6 bg-muted animate-pulse rounded-md w-32"></div>
                  <div className="space-y-4">
                    {[...Array(4)]?.map((_, i) => (
                      <div key={i} className="p-4 bg-muted/50 animate-pulse rounded-lg">
                        <div className="h-4 bg-muted rounded w-full mb-2"></div>
                        <div className="h-3 bg-muted rounded w-20"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading overlay with image */}
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 flex items-center justify-center">
            <div className="text-center">
              <img 
                src="/assets/images/image-1761885399523.png" 
                alt="Loading illustration showing data processing and analytics dashboard"
                className="w-32 h-32 mx-auto mb-4 opacity-75"
              />
              <Icon name="Loader2" size={24} className="text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground text-lg font-medium">Loading Items Management</p>
              <p className="text-muted-foreground/70 text-sm mt-1">Preparing your inventory data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleAddItem = () => {
    setSelectedItem(null);
    setIsItemModalOpen(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setIsItemModalOpen(true);
  };

  const handleDuplicateItem = async (item) => {
    try {
      const duplicatedItemData = {
        name: `${item?.name} (Copy)`,
        description: item?.description,
        sku: `${item?.sku}-COPY-${Date.now()}`,
        category: item?.category,
        price: item?.price,
        cost: item?.cost,
        unit: item?.unit,
        taxRate: item?.taxRate,
        weight: item?.weight
      };

      const { data, error } = await itemsManagementService?.createItem(organizationId, duplicatedItemData);
      
      if (error) {
        console.error('Error duplicating item:', error);
        setError('Failed to duplicate item');
      } else {
        loadItemsData(true); // Refresh with current filters
      }
    } catch (err) {
      console.error('Error in handleDuplicateItem:', err);
      setError('An unexpected error occurred while duplicating item');
    }
  };

  const handleArchiveItem = (item) => {
    setConfirmAction(() => async () => {
      try {
        const { error } = await itemsManagementService?.archiveItem(item?.id);
        
        if (error) {
          console.error('Error archiving item:', error);
          setError('Failed to archive item');
        } else {
          loadItemsData(true); // Refresh with current filters
        }
        
        setShowConfirmModal(false);
      } catch (err) {
        console.error('Error in handleArchiveItem:', err);
        setError('An unexpected error occurred while archiving item');
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setBulkDeleteItems([]);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = (selectedIds) => {
    setBulkDeleteItems(selectedIds);
    setItemToDelete(null);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    
    try {
      let deleteError = null;

      if (bulkDeleteItems?.length > 0) {
        const { error } = await itemsManagementService?.bulkDelete(bulkDeleteItems);
        deleteError = error;
      } else if (itemToDelete) {
        const { error } = await itemsManagementService?.deleteItem(itemToDelete?.id);
        deleteError = error;
      }

      if (deleteError) {
        console.error('Error deleting item(s):', deleteError);
        setError('Failed to delete item(s)');
      } else {
        loadItemsData(true); // Refresh with current filters
        loadAnalytics(); // Update analytics
      }
      
      setShowDeleteModal(false);
      setItemToDelete(null);
      setBulkDeleteItems([]);
      
    } catch (error) {
      console.error('Error deleting item(s):', error);
      setError('An unexpected error occurred while deleting item(s)');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setBulkDeleteItems([]);
    }
  };

  const getDeleteModalProps = () => {
    if (bulkDeleteItems?.length > 0) {
      return {
        title: "Delete Multiple Items",
        message: `Are you sure you want to permanently delete ${bulkDeleteItems?.length} items? This action cannot be undone.`,
        itemName: `${bulkDeleteItems?.length} items`,
        itemType: "item",
        isDangerous: bulkDeleteItems?.length > 10,
        additionalWarning: "Deleting items will remove them from all existing invoices, quotes, and purchase orders. Consider archiving instead for items with transaction history."
      };
    } else if (itemToDelete) {
      const hasTransactionHistory = itemToDelete?.lastSold !== null;
      return {
        title: "Delete Item",
        itemName: itemToDelete?.name,
        itemType: "item",
        isDangerous: hasTransactionHistory,
        additionalWarning: hasTransactionHistory 
          ? "This item has transaction history. Deleting it may affect your sales and inventory reports. Consider archiving instead."
          : null
      };
    }
    return {};
  };

  const handleSaveItem = async (itemData) => {
    try {
      let result;
      
      if (selectedItem) {
        result = await itemsManagementService?.updateItem(selectedItem?.id, itemData);
      } else {
        result = await itemsManagementService?.createItem(organizationId, itemData);
      }

      if (result?.error) {
        console.error('Error saving item:', result?.error);
        setError('Failed to save item');
      } else {
        loadItemsData(true); // Refresh with current filters
        loadAnalytics(); // Update analytics
        setIsItemModalOpen(false);
      }
    } catch (err) {
      console.error('Error in handleSaveItem:', err);
      setError('An unexpected error occurred while saving item');
    }
  };

  const handleImportItems = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => {
      const file = e?.target?.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const csvData = event?.target?.result;
            const lines = csvData?.split('\n');
            
            const importedItems = [];
            for (let i = 1; i < lines?.length; i++) {
              if (lines?.[i]?.trim()) {
                const values = lines?.[i]?.split(',')?.map(v => v?.trim());
                const itemData = {
                  name: values?.[0] || '',
                  description: values?.[1] || '',
                  sku: values?.[2] || `ITM-${Date.now() + i}`,
                  category: values?.[3] || 'General',
                  price: parseFloat(values?.[4]) || 0,
                  cost: parseFloat(values?.[5]) || 0,
                  unit: values?.[8] || 'each',
                  taxRate: parseFloat(values?.[9]) || 0,
                  weight: parseFloat(values?.[12]) || 0
                };
                
                const { data, error } = await itemsManagementService?.createItem(organizationId, itemData);
                if (!error) {
                  importedItems?.push(data);
                }
              }
            }
            
            if (importedItems?.length > 0) {
              loadItemsData(true); // Refresh with current filters
              alert(`Successfully imported ${importedItems?.length} items!`);
            }
          } catch (error) {
            console.error('Import error:', error);
            setError('Error importing file. Please check the format and try again.');
          }
        };
        reader?.readAsText(file);
      }
    };
    input?.click();
  };

  const handleExportItems = () => {
    setIsExporting(true);
    
    try {
      const csvData = itemsManagementService?.generateCSVData(processedItems);
      const csvContent = csvData?.map(row => row?.join(','))?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `items-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      alert('Items exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError('Error exporting items. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkEdit = (selectedIds) => {
    if (selectedIds?.length === 0) return;
    
    const confirmed = confirm(`Are you sure you want to edit ${selectedIds?.length} items?`);
    if (confirmed) {
      console.log('Bulk editing items:', selectedIds);
      alert('Bulk edit functionality can be implemented here');
    }
  };

  const handleBulkArchive = async (selectedIds) => {
    if (selectedIds?.length === 0) return;
    
    const confirmed = confirm(`Are you sure you want to archive ${selectedIds?.length} items?`);
    if (confirmed) {
      try {
        const { error } = await itemsManagementService?.bulkArchive(selectedIds);
        
        if (error) {
          console.error('Error bulk archiving items:', error);
          setError('Failed to archive items');
        } else {
          loadItemsData(true); // Refresh with current filters
          alert(`${selectedIds?.length} items archived successfully!`);
        }
      } catch (err) {
        console.error('Error in handleBulkArchive:', err);
        setError('An unexpected error occurred while archiving items');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Main Content */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <div className="p-6">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertCircle" size={20} className="text-error" />
                      <p className="text-error font-medium">{error}</p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setError(null)}
                        className="ml-auto"
                      >
                        <Icon name="X" size={16} />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Debug information for development */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                    <div className="font-medium text-blue-800 mb-1">Debug Info:</div>
                    <div className="text-blue-700">
                      Organization ID: {organizationId || 'Not found'}<br/>
                      Items loaded: {items?.length || 0}<br/>
                      Profile loaded: {userProfile ? 'Yes' : 'No'}<br/>
                      Is fallback: {userProfile?.isFallback ? 'Yes' : 'No'}
                    </div>
                  </div>
                )}

                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">Items Management</h1>
                    <p className="text-muted-foreground mt-1">
                      Manage your product and service catalog with inventory tracking
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button 
                      variant="outline" 
                      onClick={handleImportItems}
                      disabled={isExporting}
                    >
                      <Icon name="Upload" size={16} className="mr-2" />
                      Import Items
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleExportItems}
                      disabled={isExporting}
                    >
                      <Icon name={isExporting ? "Loader2" : "Download"} size={16} className={`mr-2 ${isExporting ? 'animate-spin' : ''}`} />
                      {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                    <Button onClick={handleAddItem}>
                      <Icon name="Plus" size={16} className="mr-2" />
                      Add Item
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                <ItemFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  itemCount={processedItems?.length}
                  categories={categories}
                />

                {/* Items Table */}
                <ItemsTable
                  items={processedItems}
                  onEdit={handleEditItem}
                  onDuplicate={handleDuplicateItem}
                  onArchive={handleArchiveItem}
                  onDelete={handleDeleteItem} 
                  onBulkEdit={handleBulkEdit}
                  onBulkArchive={handleBulkArchive}
                  onBulkDelete={handleBulkDelete}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
            </div>
          </div>

          {/* Right Sidebar - Inventory Alerts */}
          <div className="w-80 border-l border-border bg-card overflow-y-auto">
            <div className="p-6">
              <InventoryAlerts
                lowStockItems={lowStockItems}
                reorderSuggestions={analytics?.reorderSuggestions || []}
                analytics={analytics}
              />
            </div>
          </div>
        </div>
      </main>
      
      {/* Item Form Modal */}
      <ItemFormModal
        isOpen={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        item={selectedItem}
        onSave={handleSaveItem}
        categories={categories}
        organizationId={organizationId}
      />
      
      {/* Archive Confirmation Modal (existing) */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Icon name="AlertTriangle" size={24} className="text-warning" />
                <h3 className="text-lg font-medium text-foreground">Confirm Archive</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to archive this item? This will hide it from active listings but preserve transaction history.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="outline"
                  onClick={confirmAction}
                  className="bg-warning/10 text-warning hover:bg-warning/20"
                >
                  Archive Item
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal (new) */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        {...getDeleteModalProps()}
      />
    </div>
  );
};

export default ItemsManagement;